/**
 * TikTok Shop Promotion（TTS OpenAPI /promotion/202309）展示工具。
 * 活动类型 / 状态枚举以 Partner Center SearchActivities / GetActivity 为准，
 * 不再沿用 Temu 的数字活动类型与报名场次状态。
 */

import i18n from '@/i18n';
import { fixedUi } from '@/i18n/fixedUi';

/** TTS activity_type */
export const TIKTOK_ACTIVITY_TYPE_LABELS = Object.freeze({
  FIXED_PRICE: '固定价格',
  DIRECT_DISCOUNT: '直接折扣',
  FLASHSALE: '秒杀',
  SHIPPING_DISCOUNT: '运费折扣',
  BUY_MORE_SAVE_MORE: '满减优惠',
  BUY_X_GET_Y: '买X送Y',
});

/** TTS activity status */
export const TIKTOK_ACTIVITY_STATUS_LABELS = Object.freeze({
  DRAFT: '草稿',
  NOT_START: '未开始',
  ONGOING: '进行中',
  EXPIRED: '已结束',
  DEACTIVATED: '已停用',
  NOT_EFFECTIVE: '平台终止',
});

export const TIKTOK_ACTIVITY_TYPE_TABS = Object.freeze([
  { value: '', label: '全部', labelKey: 'tiktokCatalog.all' },
  { value: 'FIXED_PRICE', label: TIKTOK_ACTIVITY_TYPE_LABELS.FIXED_PRICE, labelKey: 'marketing.ttsFIXED_PRICE' },
  { value: 'DIRECT_DISCOUNT', label: TIKTOK_ACTIVITY_TYPE_LABELS.DIRECT_DISCOUNT, labelKey: 'marketing.ttsDIRECT_DISCOUNT' },
  { value: 'FLASHSALE', label: TIKTOK_ACTIVITY_TYPE_LABELS.FLASHSALE, labelKey: 'marketing.ttsFLASHSALE' },
  { value: 'SHIPPING_DISCOUNT', label: TIKTOK_ACTIVITY_TYPE_LABELS.SHIPPING_DISCOUNT, labelKey: 'marketing.ttsSHIPPING_DISCOUNT' },
  { value: 'BUY_MORE_SAVE_MORE', label: TIKTOK_ACTIVITY_TYPE_LABELS.BUY_MORE_SAVE_MORE, labelKey: 'marketing.ttsBUY_MORE_SAVE_MORE' },
  { value: 'BUY_X_GET_Y', label: TIKTOK_ACTIVITY_TYPE_LABELS.BUY_X_GET_Y, labelKey: 'marketing.ttsBUY_X_GET_Y' },
]);

export const TIKTOK_ACTIVITY_STATUS_TABS = Object.freeze([
  { value: '', label: '全部', labelKey: 'tiktokCatalog.all' },
  { value: 'DRAFT', label: TIKTOK_ACTIVITY_STATUS_LABELS.DRAFT, labelKey: 'marketing.tssDRAFT' },
  { value: 'NOT_START', label: TIKTOK_ACTIVITY_STATUS_LABELS.NOT_START, labelKey: 'marketing.tssNOT_START' },
  { value: 'ONGOING', label: TIKTOK_ACTIVITY_STATUS_LABELS.ONGOING, labelKey: 'marketing.tssONGOING' },
  { value: 'EXPIRED', label: TIKTOK_ACTIVITY_STATUS_LABELS.EXPIRED, labelKey: 'marketing.tssEXPIRED' },
  { value: 'DEACTIVATED', label: TIKTOK_ACTIVITY_STATUS_LABELS.DEACTIVATED, labelKey: 'marketing.tssDEACTIVATED' },
  { value: 'NOT_EFFECTIVE', label: TIKTOK_ACTIVITY_STATUS_LABELS.NOT_EFFECTIVE, labelKey: 'marketing.tssNOT_EFFECTIVE' },
]);

export function tiktokActivityTypeLabel(code) {
  const raw = String(code || '').trim();
  if (!raw) return '—';
  const upper = raw.toUpperCase();
  const known = TIKTOK_ACTIVITY_TYPE_LABELS[upper];
  if (known) {
    const translated = i18n.t('marketing.tts' + upper, { ns: 'workbench', defaultValue: known });
    return translated && translated !== known ? translated : fixedUi(known);
  }
  return raw;
}

export function tiktokActivityStatusLabel(code) {
  const raw = String(code || '').trim();
  if (!raw) return '—';
  const upper = raw.toUpperCase();
  const fromZh = Object.entries(TIKTOK_ACTIVITY_STATUS_LABELS).find(([, zh]) => zh === raw)?.[0];
  const keyCode = TIKTOK_ACTIVITY_STATUS_LABELS[upper] ? upper : fromZh;
  const known = keyCode ? TIKTOK_ACTIVITY_STATUS_LABELS[keyCode] : '';
  if (keyCode && known) {
    const translated = i18n.t('marketing.tss' + keyCode, { ns: 'workbench', defaultValue: known });
    return translated && translated !== known ? translated : fixedUi(known);
  }
  return raw;
}

export function tiktokActivityStatusTone(code) {
  const raw = String(code || '').trim();
  const upper = raw.toUpperCase();
  const fromZh = Object.entries(TIKTOK_ACTIVITY_STATUS_LABELS).find(([, zh]) => zh === raw)?.[0];
  const key = TIKTOK_ACTIVITY_STATUS_LABELS[upper] ? upper : (fromZh || '');
  if (key === 'ONGOING') return 'success';
  if (key === 'NOT_START' || key === 'DRAFT') return 'pending';
  if (key === 'EXPIRED' || key === 'DEACTIVATED' || key === 'NOT_EFFECTIVE') return 'failed';
  return 'neutral';
}

const ALL_LABEL = 'ALL';

function parseRawSites(rawJson) {
  if (rawJson == null || rawJson === '') return null;
  try {
    const raw = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
    if (!raw || typeof raw !== 'object') return null;
    if (!Object.prototype.hasOwnProperty.call(raw, 'sites')) return null;
    if (!Array.isArray(raw.sites)) return [];
    return raw.sites
      .map((s) => String(s?.siteName || '').trim())
      .filter(Boolean);
  } catch {
    return null;
  }
}

function splitSitesText(text) {
  return String(text || '')
    .replace(/…|\.{3}$/g, '')
    .split(/[、,，]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function isAllSitesMarker(value) {
  const t = String(value || '').trim();
  if (!t) return true;
  const lower = t.toLowerCase();
  return (
    lower === 'all'
    || t === '全部'
    || t === '全部站点'
    || t === '不限制站点'
  );
}

/**
 * @param {object} row 列表/详情行（sites / sitesFull / sitesAll / rawJson）
 * @param {{ maxVisible?: number }} [opts]
 * @returns {{ isAll: boolean, label: string, title: string }}
 */
export function formatMarketingSites(row, opts = {}) {
  const maxVisible = Number.isFinite(opts.maxVisible) && opts.maxVisible > 0 ? opts.maxVisible : 4;
  const fromRaw = parseRawSites(row?.rawJson);

  let names;
  let knownEmptyAll = false;

  if (fromRaw != null) {
    names = fromRaw;
    knownEmptyAll = fromRaw.length === 0;
  } else if (row?.sitesAll === true || isAllSitesMarker(row?.sites) || isAllSitesMarker(row?.sitesFull)) {
    return { isAll: true, label: ALL_LABEL, title: ALL_LABEL };
  } else {
    const fullText = row?.sitesFull || row?.sites || '';
    if (!String(fullText).trim()) {
      return { isAll: true, label: ALL_LABEL, title: ALL_LABEL };
    }
    names = splitSitesText(fullText);
    knownEmptyAll = names.length === 0;
  }

  if (knownEmptyAll || !names.length) {
    return { isAll: true, label: ALL_LABEL, title: ALL_LABEL };
  }

  const title = names.join('、');
  const label = names.length > maxVisible
    ? `${names.slice(0, maxVisible).join('、')}…`
    : title;
  return { isAll: false, label, title };
}

/**
 * @deprecated Temu 报名校验状态；TTS Promotion 活动详情无此字段，保留兼容旧抽屉。
 */
export function marketingCheckStatusTone(checkStatus, checkStatusDesc) {
  const code = checkStatus == null || checkStatus === '' ? NaN : Number(checkStatus);
  if (code === 1) return 'pass';
  if (code === 0) return 'fail';
  if (code === 2) return 'pending';
  if (code === 3) return 'filtered';

  const text = String(checkStatusDesc || '').trim();
  if (!text) return 'neutral';
  if (text.includes('不符合') || text.includes('不合格')) return 'fail';
  if (text === '符合' || (text.includes('合格') && !text.includes('不合格'))) return 'pass';
  if (text.includes('报名时检测')) return 'pending';
  if (text.includes('自动过滤')) return 'filtered';
  return 'neutral';
}
